import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { SeatIndicator } from "@/components/SeatIndicator";
import { ProgramDialog } from "@/components/ProgramDialog";
import { AttendeeDialog } from "@/components/AttendeeDialog";
import { Logo } from "@/components/Logo";
import { AudienceIcon, AudienceBadge, AUDIENCE_LABEL } from "@/components/AudienceIcon";
import { GuestbookHighlights } from "@/components/GuestbookHighlights";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMsg, dedupedGet, homeContentQuery } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { priceLabel } from "@/lib/price";
import {
  ArrowRight,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Clock,
  Heart,
  Sparkles,
  Ticket,
  Check,
  ChevronLeft,
  ChevronRight,
  Share2,
  Facebook,
  Instagram,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1641060889144-1cc91e6871ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  crochet: "https://images.unsplash.com/photo-1585701456523-3daa9cfee779?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  basics: "https://images.unsplash.com/photo-1519412849983-957822373d02?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  amigurumi: "https://images.unsplash.com/photo-1584992236310-6edddc08acff?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  literature: "https://images.pexels.com/photos/32759367/pexels-photo-32759367.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  owner: "https://images.unsplash.com/photo-1632649027900-389e810204e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  venue: "https://images.unsplash.com/photo-1575193506520-45ff0a377fb8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  coworking: "https://images.unsplash.com/photo-1743300873236-39e755465c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  filmclub: "https://images.unsplash.com/photo-1571947453052-1e72ce0d19b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  hotseat: "https://images.unsplash.com/photo-1778694276944-66df489166e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  literaryeve: "https://images.unsplash.com/photo-1697029749544-ffa7f15f9dd0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const SOCIAL_DEFAULTS = {
  facebook_url: "https://www.facebook.com/sorrolsorra.art",
  instagram_url: "https://www.instagram.com/sorrolsorra.art",
};

const resolveImg = (url, fallback) => {
  if (!url) return fallback;
  return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

const WORKSHOPS = [
  { title: "Horgolás kezdőknek", desc: "Megmutatom az első szemeket – tűt a kézbe, és máris horgolsz! Türelmesen, lépésről lépésre.", img: IMG.basics, color: "bg-teal", tag: "teal", audience: "adult" },
  { title: "Amigurumi műhely", desc: "Apró, cuki figurákat készítünk fonalból – garantáltan mosolyt csalnak az arcodra.", img: IMG.amigurumi, color: "bg-mustard", tag: "mustard", audience: "children" },
  { title: "Irodalmi szalon", desc: "Meghitt esték felolvasással és jó beszélgetésekkel, egy csésze tea és a fonal mellett.", img: IMG.literature, color: "bg-lilac", tag: "lilac", audience: "together" },
  { title: "#filmrőlfilmre filmklub", desc: "Együtt nézünk meg egy-egy filmet, majd meghitt hangulatban beszélgetünk róla – kávéval, fonallal, gondolatokkal.", img: IMG.filmclub, color: "bg-poppy", tag: "poppy", audience: "adult" },
  { title: "#ötrőlhatra hot seat klub", desc: "Bátor, őszinte beszélgetések a forró széken – kérdések, történetek és igazi kapcsolódás felnőtteknek.", img: IMG.hotseat, color: "bg-teal", tag: "teal", audience: "adult" },
  { title: "#könyvrőlkönyvre irodalmi est", desc: "Havonta egy könyv, egy este – felolvasás, gondolatcsere és jó társaság gyertyafény mellett.", img: IMG.literaryeve, color: "bg-lilac", tag: "lilac", audience: "adult" },
];

const PASSES = [
  { name: "1 alkalmas jegy", price: "eseményenként", period: "az adott eseményre szól", sessions: "1 alkalom", color: "#3EC5C0", popular: false, note: "Ideális, ha először néznél be hozzám.", cta: "Válassz eseményt", href: "#timetable" },
  { name: "4 alkalmas bérlet", price: "31 800 Ft", period: "35 napig érvényes", sessions: "4 alkalom", color: "#FF6B8B", popular: false, extra: "1,5 órás alkalmak (5 300 Ft/óra)", note: "Nagyszerű, ha rendszeresen jönnél.", cta: "Bérlet igénylés", href: "#passes" },
  { name: "12 alkalmas bérlet", price: "88 200 Ft", period: "100 napig érvényes", sessions: "12 alkalom", color: "#B58CE0", popular: true, extra: "1,5 órás alkalmak (4 900 Ft/óra)", note: "A legjobb ár egy alkalomra – megéri!", cta: "Bérlet igénylés", href: "#passes" },
];

const TAG_ROTATION = ["coral", "teal", "mustard", "lilac", "sky", "mint"];
const TAG_HEX = { coral: "#FF8C64", teal: "#3EC5C0", mustard: "#FFC13B", lilac: "#B58CE0", sky: "#6FB3F2", mint: "#9DD86B", poppy: "#FF6B8B" };
const AUDIENCE_HEX = { adult: "#E23D50", children: "#F59314", together: "#B58CE0" };
const audienceColor = (a) => AUDIENCE_HEX[a] || AUDIENCE_HEX.adult;
const SEAT_HEX = { green: "#6B9080", orange: "#F6AA1C", red: "#E63946", gray: "#D3D3D3" };
const SEAT_LABEL = { green: "Még van hely", orange: "Hamarosan betelik", red: "Már csak néhány hely", gray: "Betelt" };
const HU_DAYS = ["Vasárnap", "Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat"];
const huDay = (d) => { if (!d) return ""; const dt = new Date(`${d}T00:00:00`); return isNaN(dt.getTime()) ? "" : HU_DAYS[dt.getDay()]; };
const toVideoEmbed = (url) => {
  if (!url) return url;
  if (url.includes("player.vimeo.com") || url.includes("youtube.com/embed")) return url;
  let m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}?title=0&byline=0&portrait=0`;
  m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url;
};

const DOW_SHORT = ["V", "H", "K", "SZ", "CS", "P", "SZO"];
const MON_HU = ["jan.", "feb.", "márc.", "ápr.", "máj.", "jún.", "júl.", "aug.", "szept.", "okt.", "nov.", "dec."];
const toISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const rangeLabel = (days) => {
  if (!days.length) return "Nincs közelgő program";
  const a = days[0], b = days[days.length - 1];
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth())
    return `${a.getFullYear()}. ${MON_HU[a.getMonth()]} ${a.getDate()}. – ${b.getDate()}.`;
  if (a.getFullYear() === b.getFullYear())
    return `${a.getFullYear()}. ${MON_HU[a.getMonth()]} ${a.getDate()}. – ${MON_HU[b.getMonth()]} ${b.getDate()}.`;
  return `${a.getFullYear()}. ${MON_HU[a.getMonth()]} ${a.getDate()}. – ${b.getFullYear()}. ${MON_HU[b.getMonth()]} ${b.getDate()}.`;
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }) };

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`max-w-7xl mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const Overline = ({ children, color = "text-poppy" }) => <span className={`overline ${color}`}>{children}</span>;

const PassBadge = ({ passOnly, passEligible }) => {
  if (passOnly)
    return <span className="inline-flex items-center gap-1 rounded-full bg-teal/10 text-teal text-[11px] font-medium px-2 py-0.5" data-testid="card-pass-badge"><Ticket size={11} strokeWidth={1.8} /> csak bérlettel</span>;
  if (passEligible)
    return <span className="inline-flex items-center gap-1 rounded-full bg-teal/10 text-teal text-[11px] font-medium px-2 py-0.5" data-testid="card-pass-badge"><Ticket size={11} strokeWidth={1.8} /> bérlettel is</span>;
  return null;
};

const ClampDescription = ({ text, onMore }) => {
  const ref = React.useRef(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [text]);
  return (
    <div className="mt-2 flex-1">
      <p ref={ref} className="text-sm text-inkmuted leading-relaxed line-clamp-3 whitespace-pre-line">{text}</p>
      {truncated && (
        <button onClick={onMore} data-testid="featured-readmore-btn"
          className="text-sm font-medium text-poppy hover:underline">… bővebben</button>
      )}
    </div>
  );
};

const BOOK_SIZE = {
  featured: "mt-5 self-start inline-flex items-center justify-center gap-2 text-sm px-5 py-2.5",
  calsm: "mt-2 w-full text-[11px] py-1.5",
  calfull: "mt-3 w-full text-sm py-2.5",
};
const BookButton = ({ variant, booked, pending, onBook, testid }) => {
  const size = BOOK_SIZE[variant];
  if (booked) return (
    <button onClick={onBook} data-testid={testid} className={`${size} rounded-full font-medium bg-stone-100 text-ink border border-stone-300 hover:bg-stone-200 transition-colors`}>Foglalva · Módosítás</button>
  );
  if (pending) return (
    <button disabled data-testid={testid} className={`${size} rounded-full font-medium bg-poppy/60 text-white cursor-wait`}>Foglalás…</button>
  );
  return (
    <button onClick={onBook} data-testid={testid} className={`${size} group rounded-full font-medium bg-poppy text-white hover:bg-poppy/90 transition-colors`}>
      {variant === "calsm" ? "Foglalás" : "Helyfoglalás"}
      {variant === "featured" && <ArrowRight size={15} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />}
    </button>
  );
};

const FEAT_AUDIENCES = [
  { key: "all", label: "Mind" },
  { key: "children", label: "Gyerek" },
  { key: "adult", label: "Felnőtt" },
  { key: "together", label: "Közös" },
];
const FEAT_CATEGORIES = [
  { key: "all", label: "Összes" },
  { key: "handcraft", label: "Horgolás" },
  { key: "literature", label: "Irodalmi" },
  { key: "filmclub", label: "Filmklub" },
  { key: "womens_circle", label: "Női kör" },
  { key: "exhibition", label: "Kiállítás" },
  { key: "mens_circle", label: "Férfikör" },
  { key: "otrolhatra", label: "#ötrőlhatra" },
  { key: "other", label: "Egyéb" },
];
const FEAT_PER_PAGE = 6;
const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 64 : -64, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -64 : 64, opacity: 0 }),
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [weekOffset, setWeekOffset] = useState(0);
  const calTopRef = useRef(null);
  const changeWeek = (delta) => {
    setWeekOffset((o) => Math.max(0, o + delta));
    requestAnimationFrame(() => calTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const [myBookings, setMyBookings] = useState({});
  const [bookingId, setBookingId] = useState(null);
  const [audFilter, setAudFilter] = useState("all");
  const [featAud, setFeatAud] = useState("all");
  const [featCat, setFeatCat] = useState("all");
  const [featPage, setFeatPage] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [attProgram, setAttProgram] = useState(null);
  const [attBusy, setAttBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: content = {}, isFetched: contentReady } = useQuery(homeContentQuery);
  const [guestbookHighlights, setGuestbookHighlights] = useState([]);
  useEffect(() => {
    dedupedGet("/guestbook/featured")
      .then((response) => setGuestbookHighlights(response.data || []))
      .catch(() => {});
  }, []);
  const [passReq, setPassReq] = useState(null);
  const handlePassCta = (pass) => {
    if (pass.name.startsWith("1 alkalmas")) { document.querySelector("#featured")?.scrollIntoView({ behavior: "smooth" }); return; }
    const dest = "/profil?tab=passes";
    if (!user) { toast("A bérlet igényléséhez kérlek jelentkezz be vagy regisztrálj. ✿"); navigate(`/belepes?next=${encodeURIComponent(dest)}`); return; }
    navigate(dest);
  };
  const C = (k, fb) => { const v = content[k]; return (v === undefined || v === null || v === "") ? fb : v; };
  const CImg = (k, fb) => resolveImg(content[k], fb);
  const workshops = WORKSHOPS.map((w, i) => ({ ...w, title: C(`ws${i + 1}_title`, w.title), desc: C(`ws${i + 1}_desc`, w.desc), img: CImg(`ws${i + 1}_img`, w.img) }));

  const loadVersion = useRef(0);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState(false);
  const loadPrograms = async () => {
    const version = ++loadVersion.current;
    setProgramsLoading(true);
    setProgramsError(false);
    await Promise.all([
      ["/programs?section=featured", setFeatured],
      ["/programs?section=timetable", setTimetable],
    ].map(async ([url, setter]) => {
      try {
        const { data } = await dedupedGet(url);
        if (version === loadVersion.current) setter(data);
      } catch {
        if (version === loadVersion.current) setProgramsError(true);
      }
    }));
    if (version === loadVersion.current) setProgramsLoading(false);
  };
  const loadBookings = async () => {
    if (!user) { setMyBookings({}); return; }
    try {
      const { data } = await dedupedGet("/bookings/me");
      const map = {};
      data.filter((b) => ["pending", "approved", "attended"].includes(b.status))
        .forEach((b) => { map[b.program_id] = { booking_id: b.booking_id, refs: (b.attendees || []).map((a) => a.ref) }; });
      setMyBookings(map);
    } catch { /* Existing bookings stay visible during a temporary network error. */ }
  };
  const load = () => Promise.all([loadPrograms(), loadBookings()]);
  useEffect(() => { loadPrograms(); }, []); // eslint-disable-line
  useEffect(() => { loadBookings(); }, [user?.user_id]); // eslint-disable-line

  const featFiltered = featured.filter((p) =>
    (featAud === "all" || (p.audience || "together") === featAud) &&
    (featCat === "all" || (p.workshop_type || "other") === featCat)
  );
  const featPageCount = Math.max(1, Math.ceil(featFiltered.length / FEAT_PER_PAGE));
  const featSafePage = Math.min(featPage, featPageCount - 1);
  const featPageItems = featFiltered.slice(featSafePage * FEAT_PER_PAGE, featSafePage * FEAT_PER_PAGE + FEAT_PER_PAGE);
  const featTopRef = useRef(null);
  const featGo = (dir) => {
    setSlideDir(dir);
    setFeatPage((pg) => {
      const n = pg + dir;
      if (n < 0 || n >= featPageCount) return pg;
      requestAnimationFrame(() => featTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
      return n;
    });
  };
  useEffect(() => { setFeatPage(0); }, [featAud, featCat]);

  const openProgram = (p) => { setSelected(p); setDialogOpen(true); };
  const openBooking = (p) => {
    if (!user) { navigate("/belepes"); return; }
    setAttProgram(p);
  };
  const confirmBooking = async (refs) => {
    if (!attProgram) return;
    setAttBusy(true);
    setBookingId(attProgram.program_id);
    try {
      const { data } = await api.post("/bookings", { program_id: attProgram.program_id, attendees: refs });
      setMyBookings((m) => ({ ...m, [attProgram.program_id]: { booking_id: data.booking_id, refs } }));
      toast.success("Foglalás rögzítve! Jóváhagyás után e-mailt küldök.");
      setAttProgram(null);
      load();
    } catch (e) { toast.error(apiErrorMsg(e.response?.data?.detail)); }
    finally { setAttBusy(false); setBookingId(null); }
  };
  const cancelCurrentBooking = async () => {
    const b = myBookings[attProgram?.program_id];
    if (!b) return;
    if (!window.confirm("Biztosan lemondod ezt a foglalást? 24 órán belüli lemondás esetén 1 alkalom levonásra kerülhet.")) return;
    setAttBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.booking_id}/cancel`);
      toast.success(data.within_48h && data.charged ? "Lemondva – 24 órán belül, alkalom levonva." : "Foglalás lemondva.");
      setMyBookings((m) => { const n = { ...m }; delete n[attProgram.program_id]; return n; });
      setAttProgram(null);
      load();
    } catch (e) { toast.error(apiErrorMsg(e.response?.data?.detail)); }
    finally { setAttBusy(false); }
  };
  const notify = async (p) => {
    if (!user) { navigate("/belepes"); return; }
    try {
      await api.post(`/programs/${p.program_id}/notify`);
      toast.success("Értesítünk, ha felszabadul egy hely!");
    } catch (e) { toast.error(apiErrorMsg(e.response?.data?.detail)); }
  };

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email: newsletterEmail });
      toast.success("Köszönöm a feliratkozást! 💛");
      setNewsletterEmail("");
    } catch (err) { toast.error(apiErrorMsg(err.response?.data?.detail)); }
  };

  const todayISO = toISO(new Date());
  const programsForDay = (d) => timetable.filter((p) => p.start_date === toISO(d) && (audFilter === "all" || (p.audience || "together") === audFilter));
  const upcomingDates = Array.from(new Set(
    timetable
      .filter((p) => !p.cancelled && p.start_date >= todayISO && (audFilter === "all" || (p.audience || "together") === audFilter))
      .map((p) => p.start_date)
  )).sort();
  const DAYS_PER_PAGE = 7;
  const visibleDays = upcomingDates
    .slice(weekOffset * DAYS_PER_PAGE, weekOffset * DAYS_PER_PAGE + DAYS_PER_PAGE)
    .map((iso) => new Date(`${iso}T00:00:00`));
  const hasPrevWeek = weekOffset > 0;
  const hasNextWeek = upcomingDates.length > (weekOffset + 1) * DAYS_PER_PAGE;
  const weekLabel = rangeLabel(visibleDays);
  const shareProgram = async (p, e) => {
    e?.stopPropagation();
    const url = `${window.location.origin}/api/share/${p.program_id}`;
    const shareData = { title: `${p.title} · sorról sorra`, text: `${p.title} – ${p.start_date} ${p.start_time}`, url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Program link másolva – oszd meg bárhol, képes előnézettel jelenik meg! ✿");
    } catch (_) { /* megszakítva vagy nem elérhető */ }
  };

  return (
    <div className="App overflow-x-hidden" data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <div className="relative pt-[68px]">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-12 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <Overline color="text-teal">{C("hero_overline", "sok szeretettel várlak ✿")}</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight text-ink mt-3 leading-[1.02]">
              {C("hero_title", "Horgoljunk együtt,")}{" "}
              <span className="text-poppy italic">{C("hero_title_accent", "sorról sorra")}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-inkmuted leading-relaxed max-w-md">
              {C("hero_subtitle", "Egy pici, színes horgoló műhely és irodalmi szalon, ahol lelassulok, fonalat sodrok és veled együtt alkotok. Én készítek mindent, egy szál magam – gyere, foglalj helyet a következő alkalmamra!")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#featured" className="rounded-full bg-poppy text-white text-sm px-7 py-3.5 hover:bg-poppy/90 hover:-translate-y-0.5 transition-[transform,background-color] shadow-lg shadow-poppy/20" data-testid="hero-events-btn">Nézd meg az eseményeimet</a>
              <a href="#timetable" className="rounded-full border-2 border-teal text-teal text-sm px-7 py-3.5 hover:bg-teal hover:text-white transition-colors">Heti órarend</a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.6 }}
            className="relative">
            <div className="absolute -inset-3 bg-mustard/30 rounded-[2rem] rotate-3" />
            <div className="relative w-full h-[430px] rounded-[2rem] overflow-hidden shadow-xl bg-blush/50">
              {contentReady && <img src={CImg("hero_image", IMG.hero)} alt="Színes fonalak" width={1280} height={960} fetchPriority="high" decoding="async" data-testid="home-hero-image" className="w-full h-full object-cover" />}
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white px-6 py-4 rounded-2xl shadow-lg hidden sm:flex items-center gap-2 rotate-[-4deg]">
              <Heart size={18} className="text-poppy fill-poppy" />
              <span className="font-script text-2xl text-ink">{C("hero_badge", "kézzel, szívvel")}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* S2 FEATURED */}
      <Section id="featured" className="py-20 relative">
        <div ref={featTopRef} className="mb-8 scroll-mt-24">
          <Overline color="text-coral">{C("featured_overline", "amire szeretettel várlak")}</Overline>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">{C("featured_title", "Közelgő eseményeim")}</h2>
        </div>

        {/* 1. sor – célközönség szűrő (programszínek; a "Mind" fekete) */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="featured-audience-filter">
          {FEAT_AUDIENCES.map((opt) => {
            const c = opt.key === "all" ? "#1C1B19" : audienceColor(opt.key);
            const active = featAud === opt.key;
            return (
              <button key={opt.key} onClick={() => setFeatAud(opt.key)} data-testid={`featured-aud-${opt.key}`}
                style={active ? { backgroundColor: c, borderColor: c, color: "#fff" } : { borderColor: c, color: c }}
                className="rounded-full px-4 py-1.5 text-xs font-semibold border transition-opacity hover:opacity-80">
                {opt.label}
              </button>
            );
          })}
        </div>
        {/* 2. sor – kategória szűrő (kijelölve fekete) */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="featured-category-filter">
          {FEAT_CATEGORIES.map((opt) => (
            <button key={opt.key} onClick={() => setFeatCat(opt.key)} data-testid={`featured-cat-${opt.key}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${featCat === opt.key ? "bg-[#1C1B19] text-white border-[#1C1B19]" : "border-ink/15 text-ink hover:bg-stone-100"}`}>
              {opt.label}
            </button>
          ))}
        </div>

        {programsLoading && featured.length === 0 && <p role="status" data-testid="featured-loading" className="text-sm text-inkmuted">Események betöltése…</p>}
        {programsError && <button onClick={loadPrograms} data-testid="home-programs-retry" className="text-sm text-poppy underline">A programok betöltése nem sikerült. Újrapróbálás</button>}

        {!programsLoading && !programsError && (
          featFiltered.length === 0 ? (
            <p className="text-inkmuted text-base" data-testid="featured-empty">
              {featured.length === 0
                ? "Hamarosan érkeznek a következő eseményeim. ✿"
                : "Hamarosan meghirdetjük, látogass vissza kicsit később…"}
            </p>
          ) : (
            <>
              <div className="overflow-hidden">
                <AnimatePresence mode="wait" custom={slideDir} initial={false}>
                  <motion.div key={featSafePage} custom={slideDir} variants={slideVariants}
                    initial="enter" animate="center" exit="exit" transition={{ duration: 0.32, ease: "easeInOut" }}
                    drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.18}
                    onDragEnd={(e, info) => { if (info.offset.x < -80) featGo(1); else if (info.offset.x > 80) featGo(-1); }}
                    className="grid md:grid-cols-2 gap-8" data-testid="featured-grid">
                    {featPageItems.map((p) => (
                      <article key={p.program_id}
                        className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-[0_10px_40px_rgb(0,0,0,0.05)] flex flex-col md:flex-row md:min-h-[380px] hover:-translate-y-1 transition-transform"
                        data-testid="featured-card">
                        <div className="relative shrink-0 md:w-[42%]">
                          <img src={resolveImg(p.photo_url, IMG.crochet)} alt={p.title} loading="lazy" decoding="async" width={480} height={600}
                            draggable={false} data-testid={`featured-image-${p.program_id}`} className="object-cover w-full h-56 md:h-full select-none" />
                          <span className="absolute top-4 left-4 text-xs font-semibold text-white px-3 py-1 rounded-full" style={{ backgroundColor: audienceColor(p.audience) }}>
                            {p.start_date}{huDay(p.start_date) ? ` ${huDay(p.start_date)}` : ""}
                          </span>
                        </div>
                        <div className="p-7 flex flex-col flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-bold text-ink flex items-center gap-2"><Clock size={14} strokeWidth={2} />{p.start_time}–{p.end_time}</span>
                            <AudienceBadge audience={p.audience} />
                          </div>
                          <button onClick={() => openProgram(p)} data-testid="featured-title-btn" className="text-left">
                            <h3 className="font-serif text-2xl text-ink mt-2 font-medium hover:text-poppy transition-colors">{p.title}</h3>
                          </button>
                          <ClampDescription text={p.description} onMore={() => openProgram(p)} />
                          <div className="mt-3 flex items-center gap-3 flex-wrap">
                            {!p.pass_only && <span className="font-serif text-2xl font-medium text-poppy" data-testid="featured-price">{priceLabel(p.price, p.audience)}</span>}
                            <PassBadge passOnly={p.pass_only} passEligible={p.pass_eligible} />
                            <button onClick={(e) => shareProgram(p, e)} data-testid="share-btn"
                              className="ml-auto inline-flex items-center gap-1.5 text-xs text-inkmuted rounded-full border border-ink/15 px-3 py-1.5 hover:bg-stone-50 transition-colors"><Share2 size={13} /> Megosztás</button>
                          </div>
                          <div className="mt-4"><SeatIndicator info={p} showCount={false} /></div>
                          {myBookings[p.program_id] ? (
                            <BookButton variant="featured" booked onBook={() => openBooking(p)} testid="featured-book-btn" />
                          ) : p.available_seats <= 0 ? (
                            <button onClick={() => notify(p)} data-testid="featured-notify-btn"
                              className="mt-5 self-start inline-flex items-center gap-2 text-sm text-ink font-medium rounded-full px-5 py-2.5 border border-ink/15 hover:bg-stone-50 transition-colors">
                              Várólistára jelentkezem
                            </button>
                          ) : (
                            <BookButton variant="featured" pending={bookingId === p.program_id} onBook={() => openBooking(p)} testid="featured-book-btn" />
                          )}
                        </div>
                      </article>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              {featPageCount > 1 && (
                <div className="mt-10 flex items-center justify-center gap-4" data-testid="featured-pager">
                  <button onClick={() => featGo(-1)} disabled={featSafePage === 0} data-testid="featured-prev-btn" aria-label="Előző események"
                    className={`h-11 w-11 rounded-full border-2 flex items-center justify-center transition-colors ${featSafePage === 0 ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm font-semibold text-ink min-w-[64px] text-center" data-testid="featured-page-label">{featSafePage + 1} / {featPageCount}</span>
                  <button onClick={() => featGo(1)} disabled={featSafePage >= featPageCount - 1} data-testid="featured-next-btn" aria-label="További események"
                    className={`h-11 w-11 rounded-full border-2 flex items-center justify-center transition-colors ${featSafePage >= featPageCount - 1 ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </>
          )
        )}
      </Section>

      {/* S3 TIMETABLE — weekly calendar */}
      <Section id="timetable" className="py-20">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Overline color="text-sky">{C("timetable_overline", "heti programjaim")}</Overline>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">{C("timetable_title", "Mikor találkozunk?")}</h2>
            <p className="text-sm text-inkmuted mt-2">Kattints egy programra a részletekért és a foglalásért. ✿</p>
          </div>
          <div ref={calTopRef} className="flex items-center justify-center gap-4 scroll-mt-24 w-full">
            <button onClick={() => changeWeek(-1)} disabled={!hasPrevWeek} data-testid="prev-week-btn" aria-label="Előző programos napok"
              className={`h-10 w-10 rounded-full border-2 flex items-center justify-center transition-colors ${!hasPrevWeek ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
              <ChevronLeft size={18} />
            </button>
            <span className="text-lg md:text-xl font-bold text-ink min-w-[200px] text-center" data-testid="week-label">{weekLabel}</span>
            <button onClick={() => changeWeek(1)} disabled={!hasNextWeek} data-testid="next-week-btn" aria-label="Következő programos napok"
              className={`h-10 w-10 rounded-full border-2 flex items-center justify-center transition-colors ${!hasNextWeek ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="audience-filter">
          {[
            { key: "all", label: "Minden program" },
            { key: "adult", label: "Felnőtt" },
            { key: "children", label: "Gyerek" },
            { key: "together", label: "Közös" },
          ].map((opt) => {
            const c = opt.key === "all" ? "#1C1B19" : audienceColor(opt.key);
            const active = audFilter === opt.key;
            return (
              <button key={opt.key} onClick={() => setAudFilter(opt.key)} data-testid={`audience-filter-${opt.key}`}
                style={active ? { backgroundColor: c, borderColor: c, color: "#fff" } : { borderColor: c, color: c }}
                className="rounded-full px-4 py-1.5 text-xs font-semibold border transition-opacity hover:opacity-80">
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(visibleDays.length, 7) || 1}, minmax(0, 1fr))` }}>
            {visibleDays.map((d, i) => {
              const iso = toISO(d);
              const isToday = iso === todayISO;
              const dayPrograms = programsForDay(d);
              return (
                <div key={iso} data-testid="calendar-day" className={`rounded-2xl p-3 min-h-[190px] border ${isToday ? "bg-sky/10 border-sky/40" : "bg-white border-black/5"}`}>
                  <div className="text-center mb-3">
                    <div className={`text-xs font-bold tracking-widest ${isToday ? "text-sky" : "text-inkmuted"}`}>{isToday ? "MA" : DOW_SHORT[d.getDay()]} · {MON_HU[d.getMonth()]}</div>
                    <div className={`font-serif text-2xl ${isToday ? "text-sky" : "text-ink"}`}>{d.getDate()}</div>
                  </div>
                  <div className="space-y-2">
                    {dayPrograms.map((p, j) => {
                      const tag = audienceColor(p.audience);
                      return (
                        <div key={p.program_id} onClick={() => openProgram(p)} data-testid="timetable-item"
                          className="w-full text-left rounded-xl p-2.5 border-l-4 cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200"
                          style={{ borderLeftColor: tag, backgroundColor: tag + "18" }}>
                          <div className="text-xs font-bold text-ink flex items-center gap-1"><Clock size={12} strokeWidth={2} />{p.start_time}</div>
                          <div className="text-xs font-medium text-ink mt-0.5 leading-tight line-clamp-2">{p.title}</div>
                          {!p.pass_only && <div className="text-xs font-semibold mt-1" style={{ color: tag }}>{priceLabel(p.price, p.audience)}</div>}
                          {(p.pass_only || p.pass_eligible) && <div className="text-[10px] font-medium mt-0.5" style={{ color: "#2E9E99" }} data-testid="timetable-pass">{p.pass_only ? "csak bérlettel" : "bérlettel is"}</div>}
                          <div className="text-xs font-medium mt-1 flex items-center gap-1" data-testid="timetable-seat-status" style={{ color: SEAT_HEX[p.indicator] }}><span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SEAT_HEX[p.indicator] }} />{SEAT_LABEL[p.indicator]}</div>
                          <button onClick={(e) => shareProgram(p, e)} data-testid="share-btn" className="mt-1 inline-flex items-center gap-1 text-[10px] text-inkmuted hover:text-poppy transition-colors"><Share2 size={11} /> Megosztás</button>
                          {myBookings[p.program_id] ? (
                            <BookButton variant="calsm" booked onBook={(e) => { e.stopPropagation(); openBooking(p); }} testid="timetable-book-btn" />
                          ) : p.available_seats <= 0 ? (
                            <button onClick={(e) => { e.stopPropagation(); notify(p); }} data-testid="timetable-notify-btn"
                              className="mt-2 w-full rounded-full border border-ink/15 text-ink text-[11px] font-medium py-1.5 hover:bg-white transition-colors">Várólista</button>
                          ) : (
                            <BookButton variant="calsm" pending={bookingId === p.program_id} onBook={(e) => { e.stopPropagation(); openBooking(p); }} testid="timetable-book-btn" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* mobile / tablet — vertical day list */}
        <div className="lg:hidden space-y-3">
          {visibleDays.map((d, i) => {
            const iso = toISO(d);
            const isToday = iso === todayISO;
            const dayPrograms = programsForDay(d);
            return (
              <div key={iso} data-testid="calendar-day-mobile"
                className={`rounded-2xl border p-4 flex gap-4 ${isToday ? "bg-sky/10 border-sky/40" : "bg-white border-black/5"}`}>
                <div className="w-14 shrink-0 text-center">
                  <div className={`text-xs font-bold tracking-widest ${isToday ? "text-sky" : "text-inkmuted"}`}>{isToday ? "MA" : DOW_SHORT[d.getDay()]}</div>
                  <div className={`font-serif text-3xl leading-none mt-1 ${isToday ? "text-sky" : "text-ink"}`}>{d.getDate()}</div>
                  <div className="text-[10px] text-inkmuted mt-1">{MON_HU[d.getMonth()]}</div>
                </div>
                <div className="flex-1 min-w-0 space-y-3">
                  {dayPrograms.map((p, j) => {
                    const tag = audienceColor(p.audience);
                    return (
                      <div key={p.program_id} onClick={() => openProgram(p)} data-testid="timetable-item"
                        className="w-full text-left rounded-xl p-4 border-l-4 cursor-pointer"
                        style={{ borderLeftColor: tag, backgroundColor: tag + "14" }}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-ink flex items-center gap-1.5"><Clock size={14} strokeWidth={2} />{p.start_time}–{p.end_time}</span>
                          <AudienceBadge audience={p.audience} />
                        </div>
                        <h3 className="font-serif text-xl text-ink mt-1.5 font-medium">{p.title}</h3>
                        <p className="text-sm text-inkmuted mt-1 leading-relaxed line-clamp-2 whitespace-pre-line">{p.description}</p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {!p.pass_only && <span className="text-sm font-semibold" style={{ color: tag }}>{priceLabel(p.price, p.audience)}</span>}
                          <PassBadge passOnly={p.pass_only} passEligible={p.pass_eligible} />
                          <button onClick={(e) => shareProgram(p, e)} data-testid="share-btn"
                            className="ml-auto inline-flex items-center gap-1.5 text-xs text-inkmuted rounded-full border border-ink/15 px-3 py-1.5 hover:bg-stone-50 transition-colors"><Share2 size={13} /> Megosztás</button>
                        </div>
                        <div className="mt-3"><SeatIndicator info={p} showCount={false} /></div>
                        {myBookings[p.program_id] ? (
                          <BookButton variant="calfull" booked onBook={(e) => { e.stopPropagation(); openBooking(p); }} testid="timetable-book-btn" />
                        ) : p.available_seats <= 0 ? (
                          <button onClick={(e) => { e.stopPropagation(); notify(p); }} data-testid="timetable-notify-btn"
                            className="mt-3 w-full rounded-full border border-ink/15 text-ink text-sm font-medium py-2.5 hover:bg-white transition-colors">Várólistára jelentkezem</button>
                        ) : (
                          <BookButton variant="calfull" pending={bookingId === p.program_id} onBook={(e) => { e.stopPropagation(); openBooking(p); }} testid="timetable-book-btn" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {visibleDays.length > 0 && (hasPrevWeek || hasNextWeek) && (
          <div className="lg:hidden flex items-center justify-center gap-4 mt-5" data-testid="week-nav-bottom">
            <button onClick={() => changeWeek(-1)} disabled={!hasPrevWeek} data-testid="prev-week-btn-bottom" aria-label="Előző programos napok"
              className={`h-10 w-10 rounded-full border-2 flex items-center justify-center transition-colors ${!hasPrevWeek ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
              <ChevronLeft size={18} />
            </button>
            <span className="text-lg font-bold text-ink text-center">{weekLabel}</span>
            <button onClick={() => changeWeek(1)} disabled={!hasNextWeek} data-testid="next-week-btn-bottom" aria-label="Következő programos napok"
              className={`h-10 w-10 rounded-full border-2 flex items-center justify-center transition-colors ${!hasNextWeek ? "border-ink/10 text-ink/20 cursor-not-allowed" : "border-ink/40 text-ink hover:bg-ink hover:text-white"}`}>
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {programsLoading && timetable.length === 0 && <p role="status" data-testid="timetable-loading" className="text-sm text-inkmuted mt-4">Órarend betöltése…</p>}
        {!programsLoading && !programsError && timetable.length > 0 && visibleDays.length === 0 && (
          <p className="text-inkmuted text-sm mt-4" data-testid="timetable-window-empty">Jelenleg nincs közelgő programom. ✿</p>
        )}
        {!programsLoading && !programsError && timetable.length === 0 && (
          <p className="text-inkmuted text-sm mt-4">Jelenleg nincs meghirdetett programom az órarendben.</p>
        )}
      </Section>

      {/* S1 WORKSHOPS */}
      <div className="bg-blush/40 relative">
        <Section id="workshops" className="py-20">
          <div className="mb-10">
            <Overline color="text-lilac">{C("workshops_overline", "amivel foglalkozom")}</Overline>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">{C("workshops_title", "Műhelytípusaim")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {workshops.map((w, i) => (
              <motion.div key={w.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="group relative overflow-hidden rounded-3xl bg-white border border-black/5 shadow-sm" data-testid="workshop-card">
                <div className="relative overflow-hidden h-60 bg-blush/50">
                  {contentReady && <img src={w.img} alt={w.title} loading="lazy" decoding="async" width={600} height={400} data-testid={`workshop-image-${i}`} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  <div className="absolute top-4 left-4 h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: audienceColor(w.audience) }}>
                    <AudienceIcon audience={w.audience} size={18} className="text-white" strokeWidth={1.8} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-ink font-medium">{w.title}</h3>
                  <p className="mt-2 text-sm text-inkmuted leading-relaxed">{w.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* CO-WORKING */}
      <Section id="coworking" className="py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="absolute -inset-3 bg-mustard/25 rounded-[2rem] -rotate-2" />
          <div className="relative rounded-[2rem] h-[430px] w-full overflow-hidden shadow-xl bg-blush/50">
            {contentReady && <img src={CImg("coworking_image", IMG.coworking)} alt="Co-working tér" loading="lazy" decoding="async" width={1200} height={900} className="w-full h-full object-cover" data-testid="coworking-image" />}
          </div>
        </div>
        <div>
          <Overline color="text-mustard">{C("coworking_overline", "dolgozzunk együtt")}</Overline>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1" data-testid="coworking-title">{C("coworking_title", "co-working")}</h2>
          <p className="mt-4 text-base text-inkmuted leading-relaxed" data-testid="coworking-text">
            {C("coworking_text", "A nagyobbik terem mellett a kisebbik térben nyugodtan dolgozhatsz mellettem, megihatsz egy kávét, intézhetsz megbeszélést, vagy csak olvass csendben, ha úgy van kedved. Ez a tér folyamatosan változik, a munka mellett teret ad irodalmi vagy kis zenei estnek, beszélgetéseknek, vagy épp amire igény van.")}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <span className="text-base font-medium text-ink" data-testid="coworking-space-label">Irány a .space oldal →</span>
            <a
              href="https://www.sorrolsorra.space"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coworking-space-btn"
              className="inline-flex items-center justify-center rounded-full bg-ink px-7 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-black"
            >
              Foglalj helyet
            </a>
          </div>
        </div>
      </Section>

      <GuestbookHighlights entries={guestbookHighlights} />

      {/* PRICING / PASSES */}
      <Section id="passes" className="py-20">
        <div className="mb-12 text-center max-w-2xl mx-auto">
          <Overline color="text-mustard">bérletek & jegyek</Overline>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">Válaszd ki, ami neked passzol</h2>
          <p className="text-sm text-inkmuted mt-2">A fizetés első alkalommal a helyszínen. ✿</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PASSES.map((pass, i) => (
            <motion.div key={pass.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              data-testid="pass-card"
              className={`relative bg-white rounded-3xl border p-8 flex flex-col ${pass.popular ? "border-poppy shadow-[0_16px_50px_rgb(255,107,139,0.18)] md:-translate-y-3" : "border-black/5 shadow-sm"}`}>
              {pass.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-poppy text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
                  legnépszerűbb ✿
                </span>
              )}
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: pass.color }}>
                <Ticket size={20} className="text-white" />
              </div>
              <h3 className="font-serif text-2xl text-ink font-medium mt-5">{pass.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-medium" style={{ color: pass.color }}>{pass.price}</span>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {pass.extra && <li className="flex items-center gap-2 text-sm font-semibold" style={{ color: pass.color }}><Sparkles size={16} /> {pass.extra}</li>}
                <li className="flex items-center gap-2 text-sm text-ink"><Check size={16} style={{ color: pass.color }} /> {pass.period}</li>
                <li className="flex items-center gap-2 text-sm text-inkmuted"><Check size={16} style={{ color: pass.color }} /> {pass.note}</li>
              </ul>
              <button type="button" onClick={() => handlePassCta(pass)} data-testid="pass-cta"
                className={`mt-7 w-full text-center rounded-full py-3 text-sm font-medium transition-colors ${pass.popular ? "bg-poppy text-white hover:bg-poppy/90" : "border-2 text-ink hover:text-white"}`}
                style={pass.popular ? {} : { borderColor: pass.color }}
                onMouseEnter={(e) => { if (!pass.popular) e.currentTarget.style.backgroundColor = pass.color; }}
                onMouseLeave={(e) => { if (!pass.popular) e.currentTarget.style.backgroundColor = "transparent"; }}>
                {pass.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </Section>

      <Dialog open={!!passReq} onOpenChange={(o) => !o && setPassReq(null)}>
        <DialogContent data-testid="pass-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Bérlet igénylése</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      <div className="bg-mustard/20 relative">
        <Section id="owner" className="py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-3 bg-teal/25 rounded-[2rem] -rotate-2" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-video bg-ink">
              <iframe title="Rólam" className="w-full h-full" loading="lazy"
                src={toVideoEmbed(C("owner_video", "https://player.vimeo.com/video/76979871?title=0&byline=0&portrait=0"))}
                allow="autoplay; fullscreen; picture-in-picture" data-testid="owner-video" />
            </div>
          </div>
          <div>
            <Overline color="text-teal">{C("owner_overline", "rólam")}</Overline>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">{C("owner_title", "Ismerj meg engem")}</h2>
            <p className="mt-4 text-base text-inkmuted leading-relaxed">
              {C("owner_text1", "Egy szál magam viszem ezt a kis műhelyt: én tervezek, én horgolok, én főzöm a teát és én válogatom a könyveket az irodalmi estekhez. Számomra a horgolás lassítás és önkifejezés – és szeretném, ha te is megéreznéd ezt a nyugalmat.")}
            </p>
            <p className="mt-4 text-base text-inkmuted leading-relaxed">
              {C("owner_text2", "A videóban mesélek arról, hogyan indult ez az egész, és mit jelent nekem a kézzel készített szépség.")}
            </p>
            <p className="mt-5 font-script text-3xl text-poppy">{C("owner_signature", "– puszival, a te műhelyed")}</p>
          </div>
        </Section>
      </div>

      {/* S5 ABOUT PLACE */}
      <Section id="about" className="py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Overline color="text-mint">{C("venue_overline", "a helyszínem")}</Overline>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-ink mt-1">{C("venue_title", "Egy meghitt, színes sarok")}</h2>
          <p className="mt-4 text-base text-inkmuted leading-relaxed first-letter:font-serif first-letter:text-6xl first-letter:text-poppy first-letter:mr-2 first-letter:float-left first-letter:leading-none">
            {C("venue_text1", "Vidám, otthonos tér tele fonallal, kézzel válogatott apróságokkal és rengeteg természetes fénnyel. Minden sarok arra hív, hogy leülj, alkoss és elmerülj egy jó könyvben.")}
          </p>
          <p className="mt-4 text-base text-inkmuted leading-relaxed">
            {C("venue_text2", "A műhelyeim kicsik és személyesek, hogy mindenkire jusson időm és figyelmem. Kávé, tea és jó társaság nálam garantált. ☕")}
          </p>
        </div>
        <div className="relative">
          <div className="absolute -inset-3 bg-lilac/25 rounded-[2rem] rotate-2" />
          <div className="relative rounded-[2rem] h-[430px] w-full overflow-hidden shadow-xl bg-blush/50">
            {contentReady && <img src={CImg("venue_image", IMG.venue)} alt="A helyszínem" loading="lazy" decoding="async" width={1200} height={900} data-testid="home-venue-image" className="w-full h-full object-cover" />}
          </div>
        </div>
      </Section>

      {/* NEWSLETTER */}
      <div className="bg-poppy relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
          <span className="font-script text-3xl text-white/90">maradjunk kapcsolatban ✿</span>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mt-1">Iratkozz fel a hírlevelemre</h2>
          <p className="mt-3 text-white/85 text-sm">Elsőként értesülsz az új programjaimról és eseményeimről.</p>
          <form onSubmit={subscribeNewsletter} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
            <input type="email" required value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="e-mail címed" data-testid="newsletter-email"
              className="flex-1 rounded-full px-6 py-3.5 text-sm bg-white text-ink placeholder:text-inkmuted focus:outline-none focus:ring-4 focus:ring-white/40" />
            <button type="submit" data-testid="newsletter-submit"
              className="rounded-full bg-ink text-white text-sm px-7 py-3.5 hover:bg-ink/90 transition-colors">Feliratkozom</button>
          </form>
        </div>
      </div>

      {/* S6 CONTACT / FOOTER */}
      <footer id="contact" className="bg-ink text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="bg-white rounded-2xl p-4 inline-block"><Logo className="h-12 w-auto" /></div>
            <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xs">
              {C("footer_tagline", "Egy pici horgoló műhely és irodalmi szalon, ahol kézzel, szívvel készül minden. ✿")}
            </p>
          </div>
          <div className="space-y-3 text-sm">
            <h4 className="font-serif text-2xl text-mustard">Írj nekem!</h4>
            <p className="flex items-center gap-2"><MapPin size={15} strokeWidth={1.5} className="text-poppy" /> {C("contact_address", "1022 Budapest, Keleti Károly utca 48.")}</p>
            <p className="flex items-center gap-2"><Mail size={15} strokeWidth={1.5} className="text-poppy" /> {C("contact_email", "sorrolsorra@gmail.com")}</p>
            <p className="flex items-center gap-2"><Phone size={15} strokeWidth={1.5} className="text-poppy" /> {C("contact_phone", "+36 30 4567045")}</p>
            <div className="flex items-center gap-3 pt-1" data-testid="footer-social-links">
              <a
                href={C("facebook_url", SOCIAL_DEFAULTS.facebook_url)}
                target="_blank"
                rel="noreferrer"
                aria-label="sorról sorra a Facebookon"
                title="Facebook"
                data-testid="footer-facebook-link"
                className={
                  "inline-flex items-center gap-2 text-white/80 hover:text-mustard " +
                  "transition-colors"
                }
              >
                <Facebook size={17} strokeWidth={1.8} />
                <span>Facebook</span>
              </a>
              <a
                href={C("instagram_url", SOCIAL_DEFAULTS.instagram_url)}
                target="_blank"
                rel="noreferrer"
                aria-label="sorról sorra az Instagramon"
                title="Instagram"
                data-testid="footer-instagram-link"
                className={
                  "inline-flex items-center gap-2 text-white/80 hover:text-mustard " +
                  "transition-colors"
                }
              >
                <Instagram size={17} strokeWidth={1.8} />
                <span>Instagram</span>
              </a>
            </div>
            <div className="flex gap-4 pt-2 text-xs">
              <button onClick={() => setLegalOpen("terms")} data-testid="terms-link" className="underline hover:text-mustard">ÁSZF</button>
              <button onClick={() => setLegalOpen("privacy")} data-testid="privacy-link" className="underline hover:text-mustard">Adatvédelem</button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-52 border border-white/10">
            <iframe title="Térkép" className="w-full h-full" loading="lazy" data-testid="map-embed"
              src={C("map_embed", "https://www.openstreetmap.org/export/embed.html?bbox=19.0200%2C47.5100%2C19.0330%2C47.5190&layer=mapnik&marker=47.5146%2C19.0268")} />
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/60">© 2026 sorról sorra · sorrolsorra.hu · kézzel, szívvel 💛</div>
      </footer>

      <ProgramDialog program={selected} open={dialogOpen} onOpenChange={setDialogOpen} onChanged={load} />
      <AttendeeDialog open={!!attProgram} onOpenChange={(v) => { if (!v) setAttProgram(null); }}
        program={attProgram} adultName={user?.name} childrenList={user?.children || []}
        initialRefs={attProgram ? (myBookings[attProgram.program_id]?.refs || ["adult"]) : ["adult"]}
        busy={attBusy} onConfirm={confirmBooking}
        onCancel={attProgram && myBookings[attProgram.program_id] ? cancelCurrentBooking : undefined} />

      <Dialog open={!!legalOpen} onOpenChange={() => setLegalOpen(null)}>
        <DialogContent className="bg-white max-w-lg rounded-3xl" data-testid="legal-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-medium">
              {legalOpen === "terms" ? "Általános Szerződési Feltételek" : "Adatvédelmi tájékoztató"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-inkmuted leading-relaxed max-h-[60vh] overflow-y-auto">
            {legalOpen === "terms" ? (
              <p>A foglalások a jóváhagyásomat követően válnak véglegessé. A fizetés az első alkalommal a helyszínen történik.
                A foglalás lemondása az esemény előtt 24 órával díjmentes; 24 órán belüli lemondás esetén a rendszer 1 alkalmat levon a bérletedből.
                Ha egy eseményem elmarad, az érintett bérletek érvényességét egy héttel meghosszabbítom.</p>
            ) : (
              <p>A megadott személyes adataidat (név, e-mail cím) kizárólag a foglalások kezelésére és a hírlevél
                küldésére használom. Adataidat harmadik félnek nem adom ki. A hírlevélről bármikor leiratkozhatsz.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
